/**
 * @module tax
 *
 * @description
 * The merchant's tax types, which the cart reads to tax an order. A refresh that
 * fails keeps the list already held, so one dropped request does not zero the tax
 * on the orders rung until the next load.
 */
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { AppDispatch } from "../store";
import type { ITaxType } from "./types";
import { TaxAPI } from "../util/API";
import { getErrorString } from "../util/helpers";
import { dispatchFailure } from "./notify";

type TaxState = {
  list: ITaxType[];
  loading: boolean;
  error: string;
};

const initialState: TaxState = { list: [], loading: false, error: "" };

const taxSlice = createSlice({
  name: "tax",
  initialState,
  reducers: {
    start: (state): TaxState => ({ ...state, loading: true, error: "" }),
    fail: (state, action: PayloadAction<string>): TaxState => ({
      ...state,
      loading: false,
      error: action.payload,
    }),
    success: (state, action: PayloadAction<ITaxType[]>): TaxState => ({
      list: action.payload,
      loading: false,
      error: "",
    }),
  },
});
const { fail, success, start } = taxSlice.actions;
export default taxSlice.reducer;

export const fetchTaxTypes = () => {
  return async (dispatch: AppDispatch) => {
    dispatch(start());
    const onFail = (error: any) => {
      const err = getErrorString(error) || "request failed";
      dispatch(fail(err));
      // keyed: a restored session makes App load the tax types twice in a row
      dispatchFailure(
        `Tax types could not be loaded (${err}). Orders may be rung with missing or outdated tax until this page is reloaded.`,
        "tax-types-load"
      )(dispatch);
    };
    TaxAPI.fetchAll()
      .then((response) => {
        if (!getErrorString(response) && Array.isArray(response.list)) {
          dispatch(success(response.list));
        } else {
          onFail(response);
        }
      })
      .catch(onFail);
  };
};
